/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { Money } from './Money';
import {
    MoneyFromJSON,
    MoneyFromJSONTyped,
    MoneyToJSON,
} from './Money';
import type { VibInfo } from './VibInfo';
import {
    VibInfoFromJSON,
    VibInfoFromJSONTyped,
    VibInfoToJSON,
} from './VibInfo';
import type { Issuer } from './Issuer';
import {
    IssuerFromJSON,
    IssuerFromJSONTyped,
    IssuerToJSON,
} from './Issuer';
import type { SelfDisclosure } from './SelfDisclosure';
import {
    SelfDisclosureFromJSON,
    SelfDisclosureFromJSONTyped,
    SelfDisclosureToJSON,
} from './SelfDisclosure';
import type { SurveyWaiverTypeEnum } from './SurveyWaiverTypeEnum';
import {
    SurveyWaiverTypeEnumFromJSON,
    SurveyWaiverTypeEnumFromJSONTyped,
    SurveyWaiverTypeEnumToJSON,
} from './SurveyWaiverTypeEnum';
import type { CountryEnum } from './CountryEnum';
import {
    CountryEnumFromJSON,
    CountryEnumFromJSONTyped,
    CountryEnumToJSON,
} from './CountryEnum';
import type { CustomCheckbox } from './CustomCheckbox';
import {
    CustomCheckboxFromJSON,
    CustomCheckboxFromJSONTyped,
    CustomCheckboxToJSON,
} from './CustomCheckbox';
import type { RuleType } from './RuleType';
import {
    RuleTypeFromJSON,
    RuleTypeFromJSONTyped,
    RuleTypeToJSON,
} from './RuleType';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
} from './Document';
import type { InvestmentTokenForInvestor } from './InvestmentTokenForInvestor';
import {
    InvestmentTokenForInvestorFromJSON,
    InvestmentTokenForInvestorFromJSONTyped,
    InvestmentTokenForInvestorToJSON,
} from './InvestmentTokenForInvestor';
import type { AdminDistributorProductDistributionInformation } from './AdminDistributorProductDistributionInformation';
import {
    AdminDistributorProductDistributionInformationFromJSON,
    AdminDistributorProductDistributionInformationFromJSONTyped,
    AdminDistributorProductDistributionInformationToJSON,
} from './AdminDistributorProductDistributionInformation';
import type { DistributionPlatformWithPlatformType } from './DistributionPlatformWithPlatformType';
import {
    DistributionPlatformWithPlatformTypeFromJSON,
    DistributionPlatformWithPlatformTypeFromJSONTyped,
    DistributionPlatformWithPlatformTypeToJSON,
} from './DistributionPlatformWithPlatformType';

import { AdminCampaignInvitationFromJSONTyped } from './AdminCampaignInvitation';
import { AdminExternalCampaignInvitationFromJSONTyped } from './AdminExternalCampaignInvitation';
import { AdminInvestmentInvitationFromJSONTyped } from './AdminInvestmentInvitation';
/**
 * 
 * @export
 * @interface AdminInvitation
 */
export interface AdminInvitation {
    /**
     * 
     * @type {Money}
     * @memberof AdminInvitation
     */
    readonly accruedInterest: Money;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvitation
     */
    readonly agio: Money | null;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvitation
     */
    readonly canAbort: boolean;
    /**
     * For LegalPerson, this is the company_name. For NaturalPerson, it is 'forename surname'
     * @type {string}
     * @memberof AdminInvitation
     */
    readonly companyName: string;
    /**
     * If show_costs_checkbox is true, then this document needs to be shown within the checkbox.
     * @type {Document}
     * @memberof AdminInvitation
     */
    readonly costsDocument: Document;
    /**
     * ISO 3166-1 alpha-3 format
     * @type {CountryEnum}
     * @memberof AdminInvitation
     */
    readonly country: CountryEnum;
    /**
     * 
     * @type {Date}
     * @memberof AdminInvitation
     */
    readonly createdAt: Date;
    /**
     * 
     * @type {Array<CustomCheckbox>}
     * @memberof AdminInvitation
     */
    readonly customCheckboxes: Array<CustomCheckbox>;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvitation
     */
    readonly disagio: Money | null;
    /**
     * 
     * @type {AdminDistributorProductDistributionInformation}
     * @memberof AdminInvitation
     */
    readonly distributionInformation: AdminDistributorProductDistributionInformation;
    /**
     * 
     * @type {string}
     * @memberof AdminInvitation
     */
    readonly distributionPlatform: string;
    /**
     * 
     * @type {DistributionPlatformWithPlatformType}
     * @memberof AdminInvitation
     */
    readonly distributionPlatformDetails: DistributionPlatformWithPlatformType;
    /**
     * the amount of tokens the investor plans to invest (pre-configuration).
     * @type {string}
     * @memberof AdminInvitation
     */
    readonly fulfilledAmount: string | null;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvitation
     */
    readonly hasUser: boolean;
    /**
     * 
     * @type {string}
     * @memberof AdminInvitation
     */
    readonly id: string;
    /**
     * 
     * @type {string}
     * @memberof AdminInvitation
     */
    readonly investment: string | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvitation
     */
    readonly investmentTotal: Money | null;
    /**
     * 
     * @type {string}
     * @memberof AdminInvitation
     */
    readonly invitationType: string;
    /**
     * Defines whether this invitation complies with ECSP. Overrides the token config
     * @type {boolean}
     * @memberof AdminInvitation
     */
    readonly isEcsp: boolean;
    /**
     * 
     * @type {Issuer}
     * @memberof AdminInvitation
     */
    readonly issuer: Issuer;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvitation
     */
    readonly legalPersonsAllowed: boolean;
    /**
     * 
     * @type {number}
     * @memberof AdminInvitation
     */
    readonly maxNumberOfTokens: number;
    /**
     * 
     * @type {number}
     * @memberof AdminInvitation
     */
    readonly minNumberOfTokens: number;
    /**
     * 
     * @type {boolean}
     * @memberof AdminInvitation
     */
    readonly naturalPersonsAllowed: boolean;
    /**
     * 
     * @type {number}
     * @memberof AdminInvitation
     */
    readonly numberOfTokens: number;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvitation
     */
    readonly paymentTotal: Money | null;
    /**
     * 
     * @type {Money}
     * @memberof AdminInvitation
     */
    readonly pricePerToken: Money;
    /**
     * 
     * @type {string}
     * @memberof AdminInvitation
     */
    readonly productName: string;
    /**
     * rule type of invitation that defines which rules apply to check validity
     * @type {RuleType}
     * @memberof AdminInvitation
     */
    readonly ruleType: RuleType;
    /**
     * 
     * @type {SelfDisclosure}
     * @memberof AdminInvitation
     */
    readonly selfDisclosure: SelfDisclosure | null;
    /**
     * If true, and costs_document is null, frontend needs to poll for costs document until it was created successfully by the watermarking task.
     * @type {boolean}
     * @memberof AdminInvitation
     */
    readonly showCostsCheckbox: boolean;
    /**
     * 
     * @type {number}
     * @memberof AdminInvitation
     */
    readonly signedPercentage: number;
    /**
     * 
     * @type {number}
     * @memberof AdminInvitation
     */
    readonly stepSize: number;
    /**
     * survey was valid at submission time and thus no waiver is required
     * @type {boolean}
     * @memberof AdminInvitation
     */
    readonly surveyValid: boolean;
    /**
     * 
     * @type {SurveyWaiverTypeEnum}
     * @memberof AdminInvitation
     */
    readonly surveyWaiverType: SurveyWaiverTypeEnum;
    /**
     * 
     * @type {InvestmentTokenForInvestor}
     * @memberof AdminInvitation
     */
    readonly token: InvestmentTokenForInvestor;
    /**
     * 
     * @type {VibInfo}
     * @memberof AdminInvitation
     */
    readonly vibInfo: VibInfo | null;
    /**
     * 
     * @type {string}
     * @memberof AdminInvitation
     */
    readonly voucherCode: string | null;
}



/**
 * Check if a given object implements the AdminInvitation interface.
 */
export function instanceOfAdminInvitation(value: object): value is AdminInvitation {
    if (!('accruedInterest' in value) || value['accruedInterest'] === undefined) return false;
    if (!('agio' in value) || value['agio'] === undefined) return false;
    if (!('canAbort' in value) || value['canAbort'] === undefined) return false;
    if (!('companyName' in value) || value['companyName'] === undefined) return false;
    if (!('costsDocument' in value) || value['costsDocument'] === undefined) return false;
    if (!('country' in value) || value['country'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    if (!('customCheckboxes' in value) || value['customCheckboxes'] === undefined) return false;
    if (!('disagio' in value) || value['disagio'] === undefined) return false;
    if (!('distributionInformation' in value) || value['distributionInformation'] === undefined) return false;
    if (!('distributionPlatform' in value) || value['distributionPlatform'] === undefined) return false;
    if (!('distributionPlatformDetails' in value) || value['distributionPlatformDetails'] === undefined) return false;
    if (!('fulfilledAmount' in value) || value['fulfilledAmount'] === undefined) return false;
    if (!('hasUser' in value) || value['hasUser'] === undefined) return false;
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('investment' in value) || value['investment'] === undefined) return false;
    if (!('investmentTotal' in value) || value['investmentTotal'] === undefined) return false;
    if (!('invitationType' in value) || value['invitationType'] === undefined) return false;
    if (!('isEcsp' in value) || value['isEcsp'] === undefined) return false;
    if (!('issuer' in value) || value['issuer'] === undefined) return false;
    if (!('legalPersonsAllowed' in value) || value['legalPersonsAllowed'] === undefined) return false;
    if (!('maxNumberOfTokens' in value) || value['maxNumberOfTokens'] === undefined) return false;
    if (!('minNumberOfTokens' in value) || value['minNumberOfTokens'] === undefined) return false;
    if (!('naturalPersonsAllowed' in value) || value['naturalPersonsAllowed'] === undefined) return false;
    if (!('numberOfTokens' in value) || value['numberOfTokens'] === undefined) return false;
    if (!('paymentTotal' in value) || value['paymentTotal'] === undefined) return false;
    if (!('pricePerToken' in value) || value['pricePerToken'] === undefined) return false;
    if (!('productName' in value) || value['productName'] === undefined) return false;
    if (!('ruleType' in value) || value['ruleType'] === undefined) return false;
    if (!('selfDisclosure' in value) || value['selfDisclosure'] === undefined) return false;
    if (!('showCostsCheckbox' in value) || value['showCostsCheckbox'] === undefined) return false;
    if (!('signedPercentage' in value) || value['signedPercentage'] === undefined) return false;
    if (!('stepSize' in value) || value['stepSize'] === undefined) return false;
    if (!('surveyValid' in value) || value['surveyValid'] === undefined) return false;
    if (!('surveyWaiverType' in value) || value['surveyWaiverType'] === undefined) return false;
    if (!('token' in value) || value['token'] === undefined) return false;
    if (!('vibInfo' in value) || value['vibInfo'] === undefined) return false;
    if (!('voucherCode' in value) || value['voucherCode'] === undefined) return false;
    return true;
}

export function AdminInvitationFromJSON(json: any): AdminInvitation {
    return AdminInvitationFromJSONTyped(json, false);
}

export function AdminInvitationFromJSONTyped(json: any, ignoreDiscriminator: boolean): AdminInvitation {
    if (json == null) {
        return json;
    }
    if (!ignoreDiscriminator) {
        if (json['invitation_type'] === 'campaign_invitation') {
            return AdminCampaignInvitationFromJSONTyped(json, true);
        }
        if (json['invitation_type'] === 'external_campaign_invitation') {
            return AdminExternalCampaignInvitationFromJSONTyped(json, true);
        }
        if (json['invitation_type'] === 'investment_invitation') {
            return AdminInvestmentInvitationFromJSONTyped(json, true);
        }
    }
    return {
        
        'accruedInterest': MoneyFromJSON(json['accrued_interest']),
        'agio': MoneyFromJSON(json['agio']),
        'canAbort': json['can_abort'],
        'companyName': json['company_name'],
        'costsDocument': DocumentFromJSON(json['costs_document']),
        'country': CountryEnumFromJSON(json['country']),
        'createdAt': (new Date(json['created_at'])),
        'customCheckboxes': ((json['custom_checkboxes'] as Array<any>).map(CustomCheckboxFromJSON)),
        'disagio': MoneyFromJSON(json['disagio']),
        'distributionInformation': AdminDistributorProductDistributionInformationFromJSON(json['distribution_information']),
        'distributionPlatform': json['distribution_platform'],
        'distributionPlatformDetails': DistributionPlatformWithPlatformTypeFromJSON(json['distribution_platform_details']),
        'fulfilledAmount': json['fulfilled_amount'],
        'hasUser': json['has_user'],
        'id': json['id'],
        'investment': json['investment'],
        'investmentTotal': MoneyFromJSON(json['investment_total']),
        'invitationType': json['invitation_type'],
        'isEcsp': json['is_ecsp'],
        'issuer': IssuerFromJSON(json['issuer']),
        'legalPersonsAllowed': json['legal_persons_allowed'],
        'maxNumberOfTokens': json['max_number_of_tokens'],
        'minNumberOfTokens': json['min_number_of_tokens'],
        'naturalPersonsAllowed': json['natural_persons_allowed'],
        'numberOfTokens': json['number_of_tokens'],
        'paymentTotal': MoneyFromJSON(json['payment_total']),
        'pricePerToken': MoneyFromJSON(json['price_per_token']),
        'productName': json['product_name'],
        'ruleType': RuleTypeFromJSON(json['rule_type']),
        'selfDisclosure': SelfDisclosureFromJSON(json['self_disclosure']),
        'showCostsCheckbox': json['show_costs_checkbox'],
        'signedPercentage': json['signed_percentage'],
        'stepSize': json['step_size'],
        'surveyValid': json['survey_valid'],
        'surveyWaiverType': SurveyWaiverTypeEnumFromJSON(json['survey_waiver_type']),
        'token': InvestmentTokenForInvestorFromJSON(json['token']),
        'vibInfo': VibInfoFromJSON(json['vib_info']),
        'voucherCode': json['voucher_code'],
    };
}

export function AdminInvitationToJSON(value?: Omit<AdminInvitation, 'accrued_interest'|'agio'|'can_abort'|'company_name'|'costs_document'|'country'|'created_at'|'custom_checkboxes'|'disagio'|'distribution_information'|'distribution_platform'|'distribution_platform_details'|'fulfilled_amount'|'has_user'|'id'|'investment'|'investment_total'|'invitation_type'|'is_ecsp'|'issuer'|'legal_persons_allowed'|'max_number_of_tokens'|'min_number_of_tokens'|'natural_persons_allowed'|'number_of_tokens'|'payment_total'|'price_per_token'|'product_name'|'rule_type'|'self_disclosure'|'show_costs_checkbox'|'signed_percentage'|'step_size'|'survey_valid'|'survey_waiver_type'|'token'|'vib_info'|'voucher_code'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
    };
}

